import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const PermissionContext = createContext();

export const usePermissionContext = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissionContext must be used within a PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissions, setPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [businessId, setBusinessId] = useState(null);

  // Initial load on mount
  useEffect(() => {
    const token = localStorage.getItem('etpos_token');
    if (token) {
      loadUserPermissions();
    } else {
      setIsLoading(false);
    }

    // Listen for login events to reload permissions for new users
    const handleLoginEvent = () => {
      console.log('🔐 RBAC DEBUG - Login event detected, clearing and reloading permissions');
      // Clear previous user's permissions
      setPermissions([]);
      setUserRole(null);
      setBusinessId(null);
      // Reload for new user
      loadUserPermissions();
    };

    window.addEventListener('user-logged-in', handleLoginEvent);

    return () => {
      window.removeEventListener('user-logged-in', handleLoginEvent);
    };
  }, []);

  const loadUserPermissions = async () => {
    console.log('🔐 RBAC DEBUG - Loading user permissions...');
    try {
      setIsLoading(true);
      console.log('🔐 RBAC DEBUG - Calling getUserDetails...');
      const userDetails = await apiService.getUserDetails();
      console.log('🔐 RBAC DEBUG - User details received:', userDetails);
      
      // Extract actual user data from nested response
      const userData = userDetails.data || userDetails;
      console.log('🔐 RBAC DEBUG - Extracted user data:', userData);
      
      if (userData) {
        if (userData.business_id) {
          setBusinessId(userData.business_id);
          console.log('🔐 RBAC DEBUG - Business ID set:', userData.business_id);
        }
        
        // Try to get user roles from the dedicated API
        console.log('🔐 RBAC DEBUG - Fetching user roles from API...');
        try {
          const userRolesResponse = await apiService.getUserRoles(userData.business_id);
          console.log('🔐 RBAC DEBUG - User roles response:', userRolesResponse);
          
          const roles = userRolesResponse.data || userRolesResponse;
          console.log('🔐 RBAC DEBUG - Extracted roles:', roles);
          
          if (roles && roles.length > 0) {
            const role = roles[0];
            console.log('🔐 RBAC DEBUG - User role from API:', role);
            setUserRole(role);
            
            if (role.permissions) {
              const permissionNames = role.permissions.map(p => p.name);
              console.log('🔐 RBAC DEBUG - User permissions from API:', permissionNames);
              console.log('🔐 RBAC DEBUG - Permission count:', permissionNames.length);
              setPermissions(permissionNames);
            } else {
              console.log('🔐 RBAC DEBUG - No permissions found in role from API');
            }
          } else {
            console.log('🔐 RBAC DEBUG - No roles found from API, checking user data');
            await checkUserDataRoles(userData);
          }
        } catch (roleError) {
          console.error('🔐 RBAC DEBUG - Error fetching user roles from API:', roleError);
          console.log('🔐 RBAC DEBUG - Falling back to user data roles');
          await checkUserDataRoles(userData);
        }
      } else {
        console.log('🔐 RBAC DEBUG - No user details found');
      }
    } catch (error) {
      console.error('🔐 RBAC DEBUG - Error loading user permissions:', error);
      console.log('🔐 RBAC DEBUG - Error details:', error.message, error.stack);
    } finally {
      setIsLoading(false);
      console.log('🔐 RBAC DEBUG - Permission loading completed');
      console.log('🔐 RBAC DEBUG - Final permissions state will be updated asynchronously');
      // Log current state after a small delay to see final values
      setTimeout(() => {
        console.log('🔐 RBAC DEBUG - Final permissions count:', permissions.length);
        console.log('🔐 RBAC DEBUG - Final permissions:', permissions);
      }, 100);
    }
  };

  const checkUserDataRoles = async (userData) => {
    console.log('🔐 RBAC DEBUG - Checking user data roles...');
    
    // Check if user has all_permissions field (direct permissions)
    if (userData.all_permissions && Array.isArray(userData.all_permissions)) {
      console.log('🔐 RBAC DEBUG - Found all_permissions field:', userData.all_permissions);
      console.log('🔐 RBAC DEBUG - Permission count:', userData.all_permissions.length);
      
      // Set permissions directly from all_permissions
      setPermissions(userData.all_permissions);
      
      // Create a mock role object for consistency
      const mockRole = {
        id: 'direct-permissions',
        name: 'Direct Permissions',
        permissions: userData.all_permissions.map(p => ({ name: p }))
      };
      setUserRole(mockRole);
      
      console.log('🔐 RBAC DEBUG - Permissions set from all_permissions:', userData.all_permissions);
      return;
    }
    
    // Fallback to checking user data directly for roles
    if (userData.roles && userData.roles.length > 0) {
      const role = userData.roles[0];
      console.log('🔐 RBAC DEBUG - User role from user data:', role);
      setUserRole(role);
      
      if (role.permissions) {
        const permissionNames = role.permissions.map(p => p.name);
        console.log('🔐 RBAC DEBUG - User permissions from user data:', permissionNames);
        setPermissions(permissionNames);
      } else {
        console.log('🔐 RBAC DEBUG - No permissions found in role from user data');
      }
    } else {
      console.log('🔐 RBAC DEBUG - No roles or all_permissions found in user data');
      console.log('🔐 RBAC DEBUG - User data structure:', Object.keys(userData));
      console.log('🔐 RBAC DEBUG - Setting empty permissions as fallback');
      setPermissions([]);
      setUserRole(null);
    }
  };

  const hasPermission = (permission) => {
    console.log('🔐 RBAC DEBUG - Checking permission:', permission);
    console.log('🔐 RBAC DEBUG - Available permissions:', permissions);
    console.log('🔐 RBAC DEBUG - Permissions length:', permissions.length);
    console.log('🔐 RBAC DEBUG - Is Loading:', isLoading);
    
    if (!permission) {
      console.log('🔐 RBAC DEBUG - No permission specified, returning true');
      return true;
    }
    
    // If still loading, deny access to prevent showing wrong permissions
    if (isLoading) {
      console.log('🔐 RBAC DEBUG - Still loading permissions, denying access');
      return false;
    }
    
    // Check for superadmin access first
    if (permissions.includes('superadmin.access')) {
      console.log('🔐 RBAC DEBUG - Superadmin access granted');
      return true;
    }
    
    // If no permissions are set after loading, grant all access as fallback
    // This handles cases where user has loaded but permissions array is empty
    if (permissions.length === 0) {
      console.log('🔐 RBAC DEBUG - No permissions set after loading, granting access as fallback');
      return true;
    }
    
    const hasAccess = permissions.includes(permission);
    console.log('🔐 RBAC DEBUG - Permission check result:', hasAccess);
    return hasAccess;
  };

  const hasAnyPermission = (permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    if (isLoading) return false; // Deny access while loading
    if (permissions.includes('superadmin.access')) return true;
    if (permissions.length === 0) return true; // Grant access as fallback if no permissions after loading
    return permissionList.some(permission => permissions.includes(permission));
  };

  const hasAllPermissions = (permissionList) => {
    if (!permissionList || permissionList.length === 0) return true;
    if (isLoading) return false; // Deny access while loading
    if (permissions.includes('superadmin.access')) return true;
    if (permissions.length === 0) return true; // Grant access as fallback if no permissions after loading
    return permissionList.every(permission => permissions.includes(permission));
  };

  const value = {
    permissions,
    userRole,
    businessId,
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions: loadUserPermissions
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
